// Updated StoryPlayer.jsx with Instagram-like features
import React, { useState, useEffect, useRef } from 'react';
import { 
  Play, Pause, Volume2, VolumeX, 
  ChevronLeft, ChevronRight, X,
  Share2, Heart, MessageCircle, Camera, Mic
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useStoryReactions } from '../../hooks/useStoryReactions';
import FloatingReactions from './FloatingReactions';
import LiveCommentsStream from './LiveCommentsStream';
import storyReactionService from '../../services/storyReactionService';

const StoryPlayer = ({ 
  story, 
  mediaItems, 
  albumId,
  onClose, 
  autoPlay = true 
}) => {
  const { user } = useAuth();
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [showCommentsStream, setShowCommentsStream] = useState(false);
  const [comment, setComment] = useState('');
  const [isReacting, setIsReacting] = useState(false);
  
  const progressIntervalRef = useRef(null);
  const slideTimeoutRef = useRef(null);
  const audioRef = useRef(null);

  // Use real-time reactions hook
  const { 
    reactions, 
    liveReactions, 
    comments, 
    loading: reactionsLoading,
    getLikeCount,
    getCommentCount 
  } = useStoryReactions(albumId, story.id);

  const currentMediaId = story.mediaSequence[currentSlide];
  const currentMedia = mediaItems.find(m => m.id === currentMediaId);

 useEffect(() => {
    if (isPlaying) {
      startSlideTimer();
    } else {
      stopSlideTimer();
    }
    
    return () => stopSlideTimer();
  }, [isPlaying, currentSlide, story.duration]);

  useEffect(() => {
    // Setup background music
    if (story.backgroundMusic && audioRef.current) {
      audioRef.current.src = story.backgroundMusic.url;
      audioRef.current.loop = true;
      audioRef.current.muted = isMuted;
      
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
      }
    }
  }, [story.backgroundMusic, isMuted]);

  const startSlideTimer = () => {
    stopSlideTimer();
    setProgress(0);
    
    // Progress animation
    progressIntervalRef.current = setInterval(() => {
      setProgress(prev => {
        const increment = 100 / (story.duration / 100);
        return Math.min(prev + increment, 100);
      });
    }, 100);
    
    // Slide advance
    slideTimeoutRef.current = setTimeout(() => {
      goToNextSlide();
    }, story.duration);
  };

  const stopSlideTimer = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    if (slideTimeoutRef.current) {
      clearTimeout(slideTimeoutRef.current);
    }
  };

  const togglePlayback = () => {
    setIsPlaying(prev => {
      if (prev && audioRef.current) {
        audioRef.current.pause();
      } else if (!prev && audioRef.current) {
        audioRef.current.play().catch(console.error);
      }
      return !prev;
    });
  };

  const goToNextSlide = () => {
    setCurrentSlide(prev => {
      const next = prev + 1;
      if (next >= story.mediaSequence.length) {
        if (story.autoPlay) {
          return 0; // Loop back to start
        } else {
          setIsPlaying(false);
          return prev;
        }
      }
      return next;
    });
    setProgress(0);
  };

  const goToPrevSlide = () => {
    setCurrentSlide(prev => Math.max(0, prev - 1));
    setProgress(0);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setProgress(0);
  };

  const handleQuickReaction = async (type = 'like') => {
    if (!user) {
      alert('Please sign in to react');
      return;
    }

    if (isReacting) return;
    
    try {
      setIsReacting(true);
      
      const userInfo = {
        displayName: user.displayName || user.email?.split('@')[0] || 'User',
        photoURL: user.photoURL,
        email: user.email
      };
      
      switch (type) {
        case 'like':
          await storyReactionService.toggleLikeReaction(albumId, story.id, currentMediaId, user.uid, userInfo);
          break;
        case 'heart':
          await storyReactionService.createReaction(albumId, story.id, {
            type: 'like',
            mediaId: currentMediaId
          }, user.uid, userInfo);
          break;
      }
      
    } catch (error) {
      console.error('Error sending quick reaction:', error);
    } finally {
      setIsReacting(false);
    }
  };

  const handleComment = async () => {
    if (!user) {
      alert('Please sign in to comment');
      return;
    }

    if (!comment.trim()) {
      setShowCommentInput(true);
      return;
    }

    try {
      const userInfo = {
        displayName: user.displayName || user.email?.split('@')[0] || 'User',
        photoURL: user.photoURL,
        email: user.email
      };
      
      await storyReactionService.addComment(albumId, story.id, currentMediaId, comment.trim(), user.uid, userInfo);
      setComment('');
      setShowCommentInput(false);
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: story.title,
      text: story.description,
      url: `${window.location.origin}/story/${story.id}`
    };
    
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        alert('Story link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing story:', error);
    }
  };

  // Double-tap to like (mobile gesture)
  const handleDoubleTap = () => {
    handleQuickReaction('heart');
  };

  let tapTimeout = null;
  const handleTap = () => {
    if (tapTimeout) {
      clearTimeout(tapTimeout);
      tapTimeout = null;
      handleDoubleTap();
    } else {
      tapTimeout = setTimeout(() => {
        tapTimeout = null;
        // Single tap - could pause/play
        setIsPlaying(!isPlaying);
      }, 300);
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Background Music */}
      {story.backgroundMusic && (
        <audio ref={audioRef} preload="auto" />
      )}
      
      {/* Floating Reactions */}
      <FloatingReactions liveReactions={liveReactions} />
      
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 p-4">
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <div>
              <h2 className="font-semibold">{story.title}</h2>
              <p className="text-sm opacity-80">{story.description}</p>
              {/* Live reaction counts */}
              {!reactionsLoading && (
                <div className="flex items-center gap-3 text-xs opacity-70 mt-1">
                  <span>❤️ {getLikeCount()}</span>
                  <span>💬 {getCommentCount()}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {story.backgroundMusic && (
              <button
                onClick={() => setIsMuted(!isMuted)}
                className="p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 transition-colors"
              >
                {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              </button>
            )}
            
            <button
              onClick={handleShare}
              className="p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 transition-colors"
            >
              <Share2 className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Progress Bars */}
        <div className="flex gap-1 mt-4">
          {story.mediaSequence.map((_, index) => (
            <div
              key={index}
              className="flex-1 h-1 bg-white bg-opacity-30 rounded overflow-hidden"
            >
              <div 
                className="h-full bg-white transition-all duration-100"
                style={{
                  width: index < currentSlide ? '100%' : 
                         index === currentSlide ? `${progress}%` : '0%'
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Area - Double tap enabled */}
      <div 
        className="flex-1 relative flex items-center justify-center"
        onClick={handleTap}
      >
        {currentMedia && (
          <>
            <img 
              src={currentMedia.url} 
              alt={currentMedia.title}
              className="max-w-full max-h-full object-contain"
            />
            
            {/* Media Info Overlay */}
            <div className="absolute bottom-32 left-4 right-4 text-white pointer-events-none">
              <div className="bg-black bg-opacity-50 rounded-lg p-4 backdrop-blur-sm">
                <h3 className="font-semibold text-lg">{currentMedia.title}</h3>
                {currentMedia.description && (
                  <p className="text-gray-200 mt-1">{currentMedia.description}</p>
                )}
              </div>
            </div>
          </>
        )}
        
        {/* Navigation Areas */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setCurrentSlide(Math.max(0, currentSlide - 1));
            setProgress(0);
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-colors"
          disabled={currentSlide === 0}
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            setCurrentSlide(Math.min(story.mediaSequence.length - 1, currentSlide + 1));
            setProgress(0);
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-colors"
          disabled={currentSlide >= story.mediaSequence.length - 1}
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-auto">
        {/* Quick Comment Input */}
        {showCommentInput && (
          <div className="mb-4 flex gap-2">
            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 px-3 py-2 bg-white bg-opacity-20 text-white placeholder-gray-300 rounded-lg backdrop-blur-sm"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleComment();
                }
              }}
              autoFocus
            />
            <button
              onClick={handleComment}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Send
            </button>
            <button
              onClick={() => {
                setShowCommentInput(false);
                setComment('');
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
        
        <div className="flex items-center justify-between text-white">
          {/* Play Controls */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors backdrop-blur-sm"
            >
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
              <span className="text-sm">{isPlaying ? 'Pause' : 'Play'}</span>
            </button>
            
            <span className="text-sm opacity-80">
              {currentSlide + 1} of {story.mediaSequence.length}
            </span>
          </div>
          
          {/* Reaction Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleQuickReaction('like')}
              className="p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors backdrop-blur-sm"
              disabled={isReacting}
            >
              <Heart className="h-5 w-5" />
            </button>
            
            <button
              onClick={() => setShowCommentsStream(!showCommentsStream)}
              className="p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors backdrop-blur-sm relative"
            >
              <MessageCircle className="h-5 w-5" />
              {getCommentCount() > 0 && (
                <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {getCommentCount()}
                </div>
              )}
            </button>
            
            <button
              onClick={() => setShowCommentInput(!showCommentInput)}
              className="p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors backdrop-blur-sm"
            >
              <Mic className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Live Comments Stream */}
      <LiveCommentsStream
        comments={comments}
        isVisible={showCommentsStream}
        currentUserId={user?.uid}
        onToggleVisibility={() => setShowCommentsStream(!showCommentsStream)}
      />

      {/* Voiceover Audio */}
      {story.voiceover && (
        <audio 
          autoPlay={isPlaying}
          muted={isMuted}
          className="hidden"
        >
          <source src={story.voiceover.url} type="audio/webm" />
        </audio>
      )}
    </div>
  );
};

export default StoryPlayer;